import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Grid, Divider, Chip, Table, TableHead, TableBody, TableRow, TableCell, Collapse, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const formatAmount = (v) => {
  const n = parseFloat(v || 0);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const LabelValue = React.memo(({ label, value }) => (
  <Box mb={1}>
    <Typography variant="caption" color="textSecondary">{label}</Typography>
    <Typography variant="body2">{value ?? '-'}</Typography>
  </Box>
));

const Section = React.memo(({ title, children }) => (
  <Box mb={2}>
    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{title}</Typography>
    <Box p={2} border={1} borderColor="grey.300" borderRadius={1} bgcolor="#fafafa">{children}</Box>
  </Box>
));

const PaymentFields = React.memo(({ paymentForm, onPaymentFieldChange, paymentErrors, users }) => (
  <Section title="Payment Details">
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Payment Date"
          type="datetime-local"
          name="payment_date"
          value={paymentForm.payment_date || ''}
          onChange={onPaymentFieldChange}
          fullWidth
          size="small"
          required
          error={!!paymentErrors.payment_date}
          helperText={paymentErrors.payment_date}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="method-label">Method</InputLabel>
          <Select
            labelId="method-label"
            label="Method"
            name="method"
            value={paymentForm.method || 'bank_transfer'}
            onChange={onPaymentFieldChange}
          >
            <MenuItem value="cash">cash</MenuItem>
            <MenuItem value="bank_transfer_unaccountable">bank_transfer_unaccountable</MenuItem>
            <MenuItem value="bank_transfer">bank_transfer</MenuItem>
            <MenuItem value="upi">upi</MenuItem>
            <MenuItem value="cheque">cheque</MenuItem>
            <MenuItem value="gateway">gateway</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Reference"
          name="reference"
          value={paymentForm.reference || ''}
          onChange={onPaymentFieldChange}
          fullWidth
          size="small"
          required={paymentForm.method && !['cash', 'bank_transfer_unaccountable'].includes(paymentForm.method)}
          error={!!paymentErrors.reference}
          helperText={paymentErrors.reference}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth size="small" required error={!!paymentErrors.collected_by}>
          <InputLabel id="collected-by-label">Collected By</InputLabel>
          <Select
            labelId="collected-by-label"
            label="Collected By"
            name="collected_by"
            value={paymentForm.collected_by || ''}
            onChange={onPaymentFieldChange}
          >
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>
            ))}
          </Select>
          {paymentErrors.collected_by && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {paymentErrors.collected_by}
            </Typography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Notes"
          name="notes"
          value={paymentForm.notes || ''}
          onChange={onPaymentFieldChange}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
      </Grid>
    </Grid>
  </Section>
), (prevProps, nextProps) => {
  // Custom comparison: only re-render if actual values change
  return (
    prevProps.paymentForm.payment_date === nextProps.paymentForm.payment_date &&
    prevProps.paymentForm.method === nextProps.paymentForm.method &&
    prevProps.paymentForm.reference === nextProps.paymentForm.reference &&
    prevProps.paymentForm.notes === nextProps.paymentForm.notes &&
    prevProps.paymentForm.collected_by === nextProps.paymentForm.collected_by &&
    prevProps.paymentErrors.payment_date === nextProps.paymentErrors.payment_date &&
    prevProps.paymentErrors.reference === nextProps.paymentErrors.reference &&
    prevProps.paymentErrors.collected_by === nextProps.paymentErrors.collected_by &&
    prevProps.onPaymentFieldChange === nextProps.onPaymentFieldChange &&
    prevProps.users === nextProps.users
  );
});

function SummaryModal({ open, onClose, onSubmit, data, showPaymentFields = false, paymentForm = {}, onPaymentFieldChange, paymentErrors = {}, users = [], issueInvoiceSummary = false, submitLabel = 'Submit' }) {

  const services = React.useMemo(() => Array.isArray(data?.pricing_services) ? data.pricing_services : [], [data?.pricing_services]);
  const modules = React.useMemo(() => data?.modules_allowed || {}, [data?.modules_allowed]);
  const [expanded, setExpanded] = React.useState({});

  const overallTotals = React.useMemo(() => {
    return services.reduce((acc, svc) => {
      const base = parseFloat(svc.service_base || 0) || 0;
      const disc = parseFloat(svc.discount_amount || 0) || 0;
      const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
      // Prefer stored gst when present; otherwise derive from % after discount (issue invoice).
      let gst = parseFloat(svc.service_gst || 0) || 0;
      let net;
      if (issueInvoiceSummary) {
        const taxable = Math.max(base - disc, 0);
        // When GST was unchecked, service_gst is 0 and gst % is forced to 0 in summary payload.
        gst = gstPercent > 0 ? (taxable * gstPercent) / 100 : 0;
        net = taxable + gst;
      } else {
        const gross = parseFloat(svc.service_total || 0) || 0;
        net = isNaN(gross) ? 0 : gross;
      }
      acc.base += base;
      acc.gst += gst;
      acc.total += net;
      return acc;
    }, { base: 0, gst: 0, total: 0 });
  }, [services, issueInvoiceSummary]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Summary</DialogTitle>
      <DialogContent>
        <Section title={issueInvoiceSummary ? 'Issue invoice totals' : 'Overall Amount Summary'}>
          <Grid container spacing={2}>
            {data?.company_name != null && data.company_name !== '' && (
              <Grid item xs={12} sm={3}><LabelValue label="Institute" value={data.company_name} /></Grid>
            )}
            {!issueInvoiceSummary && (
              <Grid item xs={12} sm={3}><b><LabelValue label="Student Strength" value={data?.student_strength} /></b></Grid>
            )}
            <Grid item xs={12} sm={3}><LabelValue label={issueInvoiceSummary ? 'Taxable' : 'Amount'} value={formatAmount(overallTotals.base)} /></Grid>
            <Grid item xs={12} sm={3}><LabelValue label="GST" value={formatAmount(overallTotals.gst)} /></Grid>
            <Grid item xs={12} sm={3}><LabelValue label={issueInvoiceSummary ? 'Net total' : 'Total'} value={formatAmount(overallTotals.total)} /></Grid>
          </Grid>
        </Section>

        <Section title={issueInvoiceSummary ? 'Line items' : 'Services'}>
          {services.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No services selected</Typography>
          ) : issueInvoiceSummary ? (
            <Table size="small">
              <TableHead className="bold-font">
                <TableRow className="bold-font">
                  <TableCell>Service</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit price</TableCell>
                  <TableCell align="right">GST %</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell>HSN/SAC</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Net</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((svc, idx) => {
                  const base = parseFloat(svc.service_base || 0) || 0;
                  const disc = parseFloat(svc.discount_amount || 0) || 0;
                  const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
                  const taxable = Math.max(base - disc, 0);
                  const gst = gstPercent > 0 ? (taxable * gstPercent) / 100 : 0;
                  const net = taxable + gst;
                  const name = svc.display_name || svc.service_name || svc.plan_name || '-';
                  const notes = String(svc.notes || '');
                  const notesDisplay =
                    notes.startsWith('__ID_CARD_CONFIG__') || notes.startsWith('__BIOMETRIC_CONFIG__')
                      ? '-'
                      : (notes || '-');
                  const idCardItems = Array.isArray(svc.selected_id_card_items) ? svc.selected_id_card_items : [];
                  return (
                    <TableRow key={`issue-inv-${svc.order || svc.id || svc.service_id || idx}`}>
                      <TableCell>
                        <Typography variant="body2">{name}</Typography>
                        {idCardItems.length > 0 && (
                          <Box mt={0.5}>
                            {idCardItems.map((item) => (
                              <Typography
                                key={item.key || item.idx}
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {item.label} ({formatAmount(item.amount)})
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">{svc.item_count}</TableCell>
                      <TableCell align="right">{formatAmount(svc.service_amount)}</TableCell>
                      <TableCell align="right">{gstPercent}</TableCell>
                      <TableCell align="right">{formatAmount(disc)}</TableCell>
                      <TableCell>{svc.hsn_sac || '-'}</TableCell>
                      <TableCell>{notesDisplay}</TableCell>
                      <TableCell align="right">{formatAmount(net)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Table size="small">
              <TableHead className='bold-font'>
                <TableRow className='bold-font'>
                  <TableCell className='bold-font'>Service</TableCell>
                  <TableCell>Plan / feature</TableCell>
                  <TableCell>Item Count</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">GST %</TableCell>
                  <TableCell align="right">Service Total</TableCell>
                  <TableCell>Duration (Years)</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Payment Option</TableCell>
                  {/* <TableCell>Notes</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((svc, idx) => {
                  const svcKey = svc.service_id ?? idx;
                  const isOpen = !!expanded[svcKey];
                  return (
                  <React.Fragment key={`${svcKey}-${idx}`}>
                    <TableRow>
                      <TableCell>
                          <div style={{display: 'flex'}}>
                            <div style={{color: 'blue'}} onClick={() => setExpanded(prev => ({ [svcKey]: !isOpen }))}>
                              {isOpen ? 'V' : '>'}
                            </div>
                            <div style={{marginLeft: 8}}>
                              {svc.service_name || '-'}
                            </div>
                          </div>
                      </TableCell>
                      <TableCell>{svc.plan_name || svc.service_name || '-'}</TableCell>
                      <TableCell>{svc.item_count}</TableCell>
                      <TableCell align="right">{formatAmount(svc.service_amount)}</TableCell>
                      <TableCell align="right">{svc.service_gst_percentage ?? '-'}</TableCell>
                      <TableCell align="right">{formatAmount(svc.service_total)}</TableCell>
                      <TableCell>{svc.service_duration ?? '-'}</TableCell>
                      <TableCell>{svc.order_date ?? '-'}</TableCell>
                      <TableCell>{svc.service_end_date ?? '-'}</TableCell>
                      <TableCell>{svc.payment_option ?? '-'}</TableCell>
                      {/* <TableCell>{svc.notes ?? '-'}</TableCell> */}
                    </TableRow>
                    {isOpen ? <TableRow style={{ backgroundColor: 'lightgray', padding: '10px 0' }}>
                      {/* <TableCell>
                      </TableCell> */}
                      <TableCell colSpan={7}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          {(svc.payment_terms || []).length === 0 ? (
                            <Typography variant="body2" color="textSecondary">No payment terms</Typography>
                          ) : (
                            <>
                            <b>Payment Terms</b>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Term</TableCell>
                                  <TableCell>Receive Date</TableCell>
                                  <TableCell align="right">Amount %</TableCell>
                                  <TableCell align="right">Amount Rs</TableCell>
                                  <TableCell align="right">Term Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(svc.payment_terms || []).map((t, tIdx) => (
                                  <TableRow key={`${svcKey}-term-${tIdx}`}>
                                    <TableCell>{t.term_name || `Term ${tIdx + 1}`}</TableCell>
                                    <TableCell>{t.term_receive_date || '-'}</TableCell>
                                    <TableCell align="right">{t.amount_percentage ? `${t.amount_percentage}%` : '-'}</TableCell>
                                    <TableCell align="right">{t.amount_rs ? formatAmount(t.amount_rs) : '-'}</TableCell>
                                    <TableCell align="right">{formatAmount(t.amount_rs ? parseFloat(t.amount_rs || 0) : (parseFloat(svc.service_total || 0) * ((parseFloat(t.amount_percentage || 0) || 0) / 100)))}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            </>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow> : null}
                  </React.Fragment>
                )})}
              </TableBody>
            </Table>
          )}
        </Section>

        {/* <Section title="Modules">
          {Object.keys(modules).length === 0 ? (
            <Typography variant="body2" color="textSecondary">No modules configured</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.keys(modules).map((key) => (
                <Box key={key} p={1} border={1} borderColor="grey.200" borderRadius={1} bgcolor="#fff">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip label={key} size="small" color={modules[key].enabled ? 'success' : 'default'} />
                  </Box>
                  <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                    {Object.entries(modules[key].options || {}).filter(([opt, val]) => val).map(([opt]) => (
                      <Chip key={opt} label={opt} variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Section> */}

        {showPaymentFields && (
          <PaymentFields
            paymentForm={paymentForm}
            onPaymentFieldChange={onPaymentFieldChange}
            paymentErrors={paymentErrors}
            users={users}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={onSubmit} variant="contained" color="primary">{submitLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SummaryModal;
